import React from "react";
import { CopyBlock, dracula } from "react-code-blocks";
import headerImage from "../../assets/headerImage2.jpg";
import navigationImage from "../../assets/navigationImage.png";
import gitlabImage from "../../assets/gitlabImage.png";
import vscodeImage from "../../assets/vsCodeImage.png";
import sshImage from "../../assets/sshImage.png";


const Assignment2 = () => {
  return (

      <div className="container mx-auto my-8 px-5 max-w-3xl bg-gray-100 p-8 rounded-lg shadow-md">
      {/* Header */}
      <div className="relative mb-8">
        <img
          src={headerImage}
          alt="Header"
          className="w-full h-64 object-cover rounded-lg shadow-md"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
          <h1 className="text-4xl font-bold text-bg-secondary"  style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 1)' }}>1. Week - Project Management</h1>
          <p className="text-gray-300 mt-2">Published on: <span className="font-semibold">January 27, 2025</span></p>
          <div className="mt-4 flex space-x-4">
          <a
              //href="#git"
              className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary"
            >
              Git Repository
            </a>
            <a
              //href="#SSH Key"
              className="text-white bg-secondary  px-3 py-1 rounded hover:bg-secondary"
            >
              SSH Key
            </a>
            <a
              //href="#React"
              className="text-white bg-secondary px-3 py-1 rounded hover:bg-secondary"
            >
              React App
            </a>
          </div>
        </div>
      </div>
      
      {/* Übersicht */}
      <div className="text-justify h-auto mx-auto px-5 max-w-3xl">
        <p>
          The first week is about <strong>Principles and Practices</strong> and <strong>Project Management</strong>. This included setting up a version-controlled documentation website to showcase my progress throughout the course. I used <strong>Git</strong> for version control and built the website with <strong>React</strong>, leveraging its component-based structure and dynamic routing for a responsive and scalable design. <strong>Tailwind CSS</strong> was used to style the website, ensuring a modern and responsive layout.
        </p>
      </div>

      {/* Setting Up the Website with React */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">Setting Up the Website with React</h4>
        <ol className="list-decimal ml-6 mt-4">
          <li>
            <p>Create a new React app:</p>
            <CopyBlock
              text="npx create-react-app fabacademy-2025"
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Navigate to the project folder:</p>
            <CopyBlock
              text="cd fabacademy-2025"
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Install React Router for navigation:</p>
            <CopyBlock
              text="npm install react-router-dom"
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Install Tailwind CSS:</p>
            <CopyBlock
              text={`npm install -D tailwindcss postcss autoprefixer\nnpx tailwindcss init`}
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Start the development server:</p>
            <CopyBlock
              text="npm start"
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
        </ol>
      </div>

      {/* React Components Used */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">React Components Used</h4>
        <p>
          The website consists of the following components:
          <ul className="list-disc ml-6 mt-4">
            <li>Navbar: Provides navigation to all major sections of the site.</li>
            <li>Home: The landing page with an introduction to the Fab Academy 2025 project.</li>
            <li>About: Includes "About Me" and "Student Agreement" sections.</li>
            <li>Assignments: Displays all assignments in a responsive grid layout.</li>
            <li>FinalProject: Reserved for documenting the final project.</li>
          </ul>
        </p>
      </div>

      {/* Navigation */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">Navigation</h4>
        <CopyBlock
          text={`- Home: "/home"\n- About: "/about"\n- Assignments Overview: "/assignments"\n- Individual Assignments: "/assignments/:id"\n- Final Project: "/final-project"`}
          language="bash"
          showLineNumbers={false}
          theme={dracula}
          codeBlock
        />
      </div>

      {/* Technologies Used */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">Technologies Used</h4>
        <p>
          - React: Used for creating a dynamic, component-based website.<br />
          - React Router: Enables seamless navigation between pages.<br />
          - Tailwind CSS: Simplifies styling with utility-first classes.<br />
          - Git: For version control and collaboration.<br />
        </p>
      </div>
      <div className="container mx-auto px-5 max-w-3xl my-8">
      <h4 className="text-2xl font-bold mb-4">Setting up SSH Keys and Cloning the GitLab Repository</h4>
      <p>
        To securely access and manage your GitLab repository, an SSH key must be created and configured. I followed these steps to set up my SSH key and clone the repository:
      </p>
     
      <ol className="list-decimal ml-6 mt-4">
        <li>
          <p><strong>Generate an SSH Key:</strong></p>
          <p>
            Open a terminal, such as Command Prompt or PowerShell, and generate a new SSH key by running the following command:
          </p>
          <CopyBlock
            text={`ssh-keygen -t ed25519 -C "your@mail.com"`}
            language="bash"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />
          <p>
            This will create a private and a public key. By default, the files are saved in the <code>~/.ssh/</code> directory.
          </p>
        </li>

        <li>
          <p><strong>Add the Public Key to GitLab:</strong></p>
          <p>
            - Copy the contents of the public key file (e.g., <code>id_ed25519.pub</code>).<br />
            - Log in to your GitLab profile and click on your profile picture in the top-right corner.<br />
            <img src={sshImage} alt="GitLab Deployment" className="rounded-lg shadow-md my-4" />
            - Navigate to <strong>Edit Profile</strong> → <strong>SSH Keys</strong>, paste the public key into the field, and confirm the action.
          </p>
        </li>

        <li>
          <p><strong>Create a Configuration File:</strong></p>
          <p>
            To streamline connections to GitLab, create a configuration file named <code>config</code> (without a file extension) in the <code>~/.ssh/</code> directory. Add the following lines to the file:
          </p>
          <CopyBlock
            text={`Host gitlab.fabcloud.org
    HostName gitlab.fabcloud.org
    User yourusername
    IdentityFile ~/.ssh/id_ed25519
    IdentitiesOnly yes`}
            language="text"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />
        </li>

        <li>
          <p><strong>Test the SSH Connection:</strong></p>
          <p>Test the connection to the GitLab server by running the following command:</p>
          <CopyBlock
            text={`ssh -T git@gitlab.fabcloud.org`}
            language="bash"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />
          <p>
            If the connection is successful, you will see a message like:
          </p>
          <CopyBlock
            text={`Welcome to GitLab, @yourUsername!`}
            language="text"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />
        </li>

        <li>
          <p><strong>Clone the Repository:</strong></p>
          <p>
            After setting up the SSH key, you can clone your repository with the following command:
          
          </p>
          <CopyBlock
            text={`git clone git@gitlab.fabcloud.org:academany/fabacademy/2025/labs/bottrop/students/yourUserName.git`}
            language="bash"
            showLineNumbers={false}
            theme={dracula}
            codeBlock
          />
        </li>
      </ol>
    </div>
   

            {/* Version Control with Git */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">Version Control with Git</h4>
        <p>Below are the steps I followed to set up and manage my project with Git:</p>
        <ol className="list-decimal ml-6 mt-4">
          <li>
            <p>Initialize a new Git repository:</p>
            <CopyBlock
              text="git init"
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Add all files to the staging area:</p>
            <CopyBlock
              text="git add ."
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Make the first commit:</p>
            <CopyBlock
              text='git commit -m "Initial commit - set up React project"'
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
          <li>
            <p>Push the code to a GitHub repository:</p>
            <CopyBlock
              text={`git remote add origin https://github.com/<your-username>/fabacademy-2025.git\ngit push -u origin main`}
              language="bash"
              showLineNumbers={false}
              theme={dracula}
              codeBlock
            />
          </li>
        </ol>
      </div>

    <div className="container mx-auto my-8 px-5 max-w-3xl bg-dracula p-8 rounded-lg shadow-md">
      {/* Header */}
      <h4 className="text-2xl font-bold mb-4">Building My React App</h4>
      <p className="mb-4 text-justify text-dracula-comment">
        This documentation describes how I developed my web app using React and React Router, and how I deployed it to GitLab Pages by copying the build output to the public folder.
      </p>

      {/* Step 1: Setting Up React Project */}
      <h4 className="text-2xl font-bold mb-4">1. Setting Up the React Project</h4>
      <p className="text-justify text-dracula-comment">
        I initialized a new React app using <code className="text-dracula-green">create-react-app</code> and configured React Router for navigation. 
      </p>
      <img src={vscodeImage} alt="VSCode Setup" className="rounded-lg shadow-md my-4" />

      {/* Step 2: Navigation with React Router */}
      <h4 className="text-2xl font-bold mb-4">2. Implementing Navigation</h4>
      <p className="text-justify text-dracula-comment">
        The navigation was created using <code className="text-dracula-green">react-router-dom</code> to allow seamless routing between pages.
      </p>
      <h4 className="text-2xl font-bold mb-4">React App Structure</h4>

{/* App.js */}
<h4 className="text-2xl font-bold mb-4">App.js</h4>
<CopyBlock
  text={`
const App = () => {
return (
<div>
<Navbar />
<Routes>
  <Route path="/" element={<Navigate to="/home" />} />
  <Route path="/home" element={<Home />} />
  <Route path="/about" element={<About />} />
  <Route path="/final-project" element={<FinalProject />} />
  <Route path="/assignments" element={<Assignments />} />
  <Route path="/assignments/1" element={<Assignment1 />} />
  <Route path="/assignments/2" element={<Assignment2 />} />
  <Route path="/assignments/3" element={<Assignment3 />} />
  <Route path="/assignments/4" element={<Assignment4 />} />
  <Route path="/assignments/5" element={<Assignment5 />} />
  <Route path="/assignments/6" element={<Assignment6 />} />
  <Route path="/assignments/7" element={<Assignment7 />} />
  <Route path="/assignments/8" element={<Assignment8 />} />
  <Route path="/assignments/9" element={<Assignment9 />} />
  <Route path="/assignments/10" element={<Assignment10 />} />
  <Route path="/assignments/11" element={<Assignment11 />} />
  <Route path="/assignments/12" element={<Assignment12 />} />
  <Route path="/assignments/13" element={<Assignment13 />} />
  <Route path="/assignments/14" element={<Assignment14 />} />
  <Route path="/assignments/15" element={<Assignment15 />} />
  <Route path="/assignments/16" element={<Assignment16 />} />
  <Route path="/assignments/17" element={<Assignment17 />} />
  <Route path="/assignments/18" element={<Assignment18 />} />
  <Route path="/assignments/19" element={<Assignment19 />} />
  <Route path="/assignment/20" element={<FinalProject />} />
</Routes>
</div>
);
};

export default App;
`}
  language="jsx"
  showLineNumbers={true}
  theme={dracula}
  codeBlock
/>

{/* Navbar.js */}
<h4 className="text-2xl font-bold mt-8 mb-4">Navbar.js</h4>
<CopyBlock
  text={`
import React, { useState } from "react";
import { Link } from "react-router-dom";

const Navbar = () => {
const [isOpen, setIsOpen] = useState(false); 

const toggleMenu = () => {
setIsOpen(!isOpen);
};

return (
<nav className="bg-teal-500 text-white shadow-md">
<div className="container mx-auto px-6 py-4 flex justify-between items-center">
  {/* Logo */}
  <h1 className="text-2xl font-bold text-white">
    <Link to="/home">Fab Academy 2025</Link>
  </h1>

  {/* Hamburger Button */}
  <button
    onClick={toggleMenu}
    className="text-white focus:outline-none sm:hidden"
    aria-label="Toggle Menu"
  >
    <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  </button>

  {/* Desktop Navigation */}
  <div className="hidden sm:flex space-x-4">
    <Link to="/home" className="hover:text-gray-200">Home</Link>
    <Link to="/assignments" className="hover:text-gray-200">Assignments</Link>
    <Link to="/final-project" className="hover:text-gray-200">Final Project</Link>
    <Link to="/about" className="hover:text-gray-200">About Me</Link>
  </div>
</div>

{/* Mobile Dropdown Navigation */}
<div className={isOpen ? "block" : "hidden sm:hidden bg-gray-400 text-white"}>
  <ul className="flex flex-col">
    <li><Link to="/home" onClick={() => setIsOpen(false)}>Home</Link></li>
    <li><Link to="/about" onClick={() => setIsOpen(false)}>About Me</Link></li>
    <li><Link to="/assignments" onClick={() => setIsOpen(false)}>Assignments</Link></li>
    <li><Link to="/final-project" onClick={() => setIsOpen(false)}>Final Project</Link></li>
  </ul>
</div>
</nav>
);
};

export default Navbar;
`}
  language="jsx"
  showLineNumbers={true}
  theme={dracula}
  codeBlock
/>
</div>
<div>
<p>Here you can see the structure of my Assigments Page, that navigates through the different weeks of the Fabacademy.</p>
<h4 className="text-2xl font-bold mt-8 mb-4">Assignments.js</h4>
<CopyBlock
  text=
{`const Assignments = () => {
  return (
    <div className="container mx-auto my-8">
      <h2 className="text-3xl font-bold mb-6 text-center">Assignments</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {assignments.map((assignment) => (
          <div key={assignment.id} className="block bg-white shadow-lg rounded-lg overflow-hidden hover:shadow-xl transition-shadow">
            <div className="bg-teal-500 text-white text-center py-2">
              <p className="font-bold">{assignment.week}</p>
            </div>
            <Link to={assignment.id === 20 ? "/final-project" : "/assignments/" + assignment.id}>
              <img
                src={assignment.image}
                alt={assignment.title}
                className="w-full h-32 object-contain bg-gray-100"
              />
              <div className="p-4 text-center">
                <h3 className="text-lg font-bold">{assignment.title}</h3>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Assignments;`}
  language="jsx"
  showLineNumbers={true}
  theme={dracula}
  codeBlock
/>
</div>



<p>Here you can see the hamburger menu for mobile devices.</p>
      <img src={navigationImage} alt="Navigation Code" className="rounded-lg shadow-md my-4" />
      
      {/* Step 3: Deploying to GitLab Pages */}
      <h4 className="text-2xl font-bold mb-4">3. Deploying to GitLab Pages</h4>
      <p className="text-justify text-dracula-comment">
        To make the app accessible online, I built the project and copied the contents (!) of the <code className="text-dracula-green">build</code> folder into <code className="text-dracula-green">public</code> in my GitLab repository.
        It took some time for me to realise, that I don't have to copy the entire folder into the public folder. So if your page is not running properly on the server, that might be an issue. 
      </p>
      <img src={gitlabImage} alt="GitLab Deployment" className="rounded-lg shadow-md my-4" />
      
      
      <p className="text-justify text-dracula-comment">
        This configuration ensured my React app was built and served from GitLab Pages correctly.
      </p>
     
      {/* Outcome */}
      <div className="h-auto mx-auto px-5 max-w-3xl my-8">
        <h4 className="text-2xl font-bold mb-4">Outcome</h4>
        <p>I successfully set up a documentation website this week! I am looking forward to continue developing and bringing life to it!</p>
        </div>
      </div>

      )
};
export default Assignment2;